<template>
    <div
        ref="accordeon"
        class="ui-card-accordeon"
    >
        <UiTeleport>
            <div ref="accordeonWrapper" class="ui-card-accordeon__wrapper">
                <div
                    ref="wrappers"
                    v-for="(item, i) in items"
                    :key="item.value"
                    class="ui-card-accordeon__card-wrapper"
                >
                    <div
                        ref="cards"
                        class="ui-card-accordeon__card"
                    >
                        <div ref="prepends">
                            <slot :name="`prepend-${item.value}`" />
                            <div
                                class="ui-card-accordeon__switcher _dflex _align-items-center _pl-56-16"
                                @click="onClickCard(i)"
                            >
                                <div class="icon icon-plus icon_size-32 icon_white icon_circle" />
                                <div class="p1 white-text _ml-16">{{ item.title }}</div>
                            </div>
                        </div>
                        <slot :name="`card-${item.value}`" />
                    </div>
                </div>
            </div>
        </UiTeleport>
    </div>
</template>
<script>
import { ref, computed, watch, onMounted, set, reactive } from 'vue';
import { getRect } from '../../assets/js/vue/utlis/getRect';
import { useStore } from '../../store';
import UiTeleport from './UiTeleport.vue';
import fastdom from 'fastdom';
import { useResizeObserver } from '../../assets/js/vue/utlis/useResizeObserver';
import { useScroll } from '../../assets/js/vue/utlis/useScroll';
import { useIntersectionObserver } from '../../assets/js/vue/utlis/useIntersectionObserver';

export default {
    name: 'UiCardAccordeon',
    components: {
        UiTeleport,
    },
    setup(props) {
        const accordeon = ref(null);
        const accordeonWrapper = ref(null);
        const wrappers = ref(null);
        const cards = ref(null);
        const prepends = ref(null);
        /**
         * высота хэдера карт
         */
        const prependHeights = ref([]);

        /**
         * если слайд "приклеен", то 1 иначе, 0
         */
        const progress = ref([]);

        const store = useStore();

        const isMobile = computed(() => store.state.app.isMobile);
        const headerHeight = computed(() => store.state.app.headerFloatHeight);
        const viewportHeight = computed(() => store.state.app.windowHeight);
        const viewportWidth = computed(() => store.state.app.windowWidth);

        const cardHeights = ref([]);
        let accordeonRect = reactive({
            pageTop: 0,
            pageLeft: 0,
            width: 0,
        });

        const measureAccordeonRect = () => {
            fastdom.measure(() => {
                const { scrollY, scrollX } = window;
                const { top, left } = accordeon.value.getBoundingClientRect();
                accordeonRect.pageTop = scrollY + top;
                accordeonRect.pageLeft = scrollX + left;
            });
        }


        useScroll(window, () => {
            if (!accordeon.value) return;

            measureAccordeonRect();
        });

        watch([viewportWidth, viewportHeight, headerHeight], () => {
            measureAccordeonRect();
        }, { immediate: true });

        /**
         * доп. смещение на высоту хэдера карты
         */
        const prependOffsetSummary = computed(() => {
            const prependOffsets = [];
            const result = [];

            prependHeights.value.forEach((height, i) => {
                prependOffsets[i + 1] = height ?? 0;
                result[i] = prependOffsets.slice(0, i + 1).reduce((cur, acc) => acc + (cur ?? 0), 0);
            });

            return result;
        });

        /**
         * итоговое смещение аккордеона от верхнего края окна
         */
        const topOffsetCurrent = computed(() => headerHeight.value);

        useResizeObserver(prepends, (entries) => {
            entries.forEach((entry, i) => {
                if (entry.borderBoxSize?.[0]) { // supported ios >=15.4
                    const { blockSize } = entry.borderBoxSize[0];
                    set(prependHeights.value, i, blockSize);
                } else {
                    fastdom.measure(() => {
                        const { height } = entry.target.getBoundingClientRect();
                        headerHeight.value = height;
                    });
                }
            });
        }, { box: 'border-box' });

        useResizeObserver(cards, (entries) => {
            entries.forEach((entry, i) => {
                if (entry.borderBoxSize?.[0]) { // supported ios >=15.4
                    const { blockSize } = entry.borderBoxSize[0];

                    fastdom.measure(() => {
                        const { marginTop, marginBottom } = window.getComputedStyle(entry.target);

                        const height = blockSize + Number.parseFloat(marginTop) + Number.parseFloat(marginBottom);
                        set(cardHeights.value, i, height);
                    });
                } else {
                    fastdom.measure(() => {
                        const { height: blockSize } = entry.target.getBoundingClientRect();
                        const { marginTop, marginBottom } = window.getComputedStyle(entry.target);

                        const height = blockSize + Number.parseFloat(marginTop) + Number.parseFloat(marginBottom);
                        set(cardHeights.value, i, height);
                    });
                }
            });
        }, { box: 'border-box' });

        useResizeObserver(accordeon, (entries) => {
            for (let entry of entries) {
                if (entry.borderBoxSize?.[0]) { // supported ios >=15.4
                    accordeonRect.width = entry.borderBoxSize[0].inlineSize;
                } else {
                    fastdom.measure(() => {
                        const { width } = entry.target.getBoundingClientRect();
                        accordeonRect.width = width;
                    });
                }
            }
        });

        const getOffset = (i) => {
            const offset = topOffsetCurrent.value + (prependOffsetSummary.value[i] ?? 0) - Math.max(topOffsetCurrent.value + (prependOffsetSummary.value[i] ?? 0) + (cardHeights.value[i] ?? 0) - viewportHeight.value, 0);
            return offset;
        };

        const getTop = (i) => {
            return cardHeights.value.reduce((acc, cur, ii) => {
                if (ii > i) return acc;
                return acc + cur;
            }, 0)
        };

        const getHeight = (i) => {
            return cardHeights.value.slice(i, -1).reduce((acc, cur) => acc + cur, 0) - prependHeights.value.slice(i, -1).reduce((acc, cur) => acc + cur, 0) + cardHeights.value[i];
        };

        const cardsObserverStoppers = [];
        const startCardsObserver = () => {

            cards.value?.forEach((card, i) => {
                if (typeof cardsObserverStoppers[i] === 'function') cardsObserverStoppers[i]();

                const offset = getOffset(i) || 0;
                const top = cardHeights.value[i] || 0;
                const bottom = viewportHeight.value - (offset + cardHeights.value[i]) || 0;
                const rootMargin = `${top}px 0px -${bottom}px 0px`;

                const { stop } = useIntersectionObserver(
                    card,
                    ([entry]) => {
                        const intersect = entry.intersectionRatio === 1;
                        set(progress.value, i, intersect ? 1 : 0);
                    },
                    {
                        threshold: [1],
                        rootMargin,
                    }
                );
                cardsObserverStoppers[i] = stop;
            });
        };

        const refreshSticky = () => {
            fastdom.mutate(() => {
                accordeon.value.style.height = `${cardHeights.value.reduce((acc, cur) => acc + cur, 0)}px`;
                accordeonWrapper.value.style.left = `${accordeonRect.pageLeft}px`;
                accordeonWrapper.value.style.width = `${accordeonRect.width}px`;
                accordeonWrapper.value.style.top = `${accordeonRect.pageTop}px`;
                wrappers.value.forEach((el, i) => {
                    el.style.top = `${i === 0 ? 0 : getTop(i-1)}px`;
                    el.style.height = `${getHeight(i)}px`;
                    el.style.width = `${accordeonRect.width}px`;
                    cards.value[i].style.top = `${getOffset(i)}px`;
                });
            });
        };

        const onResize = () => {
            if (isMobile.value && props.disableOnMobile) return;
            refreshSticky();
            startCardsObserver();
        };

        watch([topOffsetCurrent, accordeonRect, cardHeights], () => {
            onResize();
        }, { deep: true, immediate: true });

        onMounted(() => {
            onResize();
        });

        return {
            accordeon,
            accordeonWrapper,
            wrappers,
            cards,
            prepends,
            prependOffsetSummary,
            topOffsetCurrent,
            progress,
            isMobile,
            cardHeights,
            prependHeights,
            accordeonRect,
        };
    },
    props: {
        items: { type: Array, default: () => [], required: true },
        disableOnMobile: Boolean,
    },
    watch: {
        progress: {
            deep: true,
            immediate: true,
            handler(val) {
                this.$emit('progress', val);
            },
        },
    },
    methods: {
        async onClickCard(i) {
            const { top } = await getRect(this.wrappers[i]);
            const offset = this.isMobile ? this.topOffsetCurrent : this.topOffsetCurrent + this.prependOffsetSummary[i];
            const toTop = window.scrollY + top - offset;
            window.scrollTo({
                behavior: 'smooth',
                top: toTop,
            });
        },
    },
};
</script>
<style lang="scss">
@import '../../assets/scss/base/variables';
@import '../../assets/scss/base/functions';
@import '../../assets/scss/base/mixins';

.ui-card-accordeon {
    @include fluid('margin-bottom', -32px);

    &__wrapper {
        position: absolute;
        @include fluid('--ui-card-accordeon-h-h', 88px, 80px);
    }

    &__card-wrapper {
        pointer-events: none;
        position: absolute;
    }

    &__card {
        @include fluid('padding-bottom', 32px);
        pointer-events: all;
        z-index: 1;
        position: sticky;
    }

    &__switcher {
        height: var(--ui-card-accordeon-h-h);
        position: relative;
        z-index: 1;
        cursor: pointer;
    }
}
</style>
